import { useState } from 'react';
import { useSnackbar } from 'notistack';
import {
  useAddParticipants,
  useSupplierCandidates,
  useSupplierContacts,
} from '@/api/sourcing/sourcing.hooks';
import { DsButton, DsModal, DsSearchInput } from '../../ds';

interface Props {
  tenderId: string;
  existingSupplierIds: number[];
  onClose: () => void;
}

export function ParticipantPicker({
  tenderId,
  existingSupplierIds,
  onClose,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [q, setQ] = useState('');
  const { data: suppliers, isFetching } = useSupplierCandidates(q);
  const [supplierId, setSupplierId] = useState<number | undefined>(undefined);
  const [contactId, setContactId] = useState<number | undefined>(undefined);
  const { data: contacts } = useSupplierContacts(supplierId);
  const addParticipants = useAddParticipants();

  const existing = new Set(existingSupplierIds);
  const selectedSupplier = suppliers?.find((s) => s.id === supplierId);

  const handleAdd = () => {
    if (!supplierId) return;
    addParticipants.mutate(
      {
        id: tenderId,
        body: { participants: [{ supplierId, contactId }] },
      },
      {
        onSuccess: (added) => {
          enqueueSnackbar(
            `${added[0]?.supplierName ?? 'Lieferant'} hinzugefügt.`,
            { variant: 'success' },
          );
          setSupplierId(undefined);
          setContactId(undefined);
          setQ('');
        },
      },
    );
  };

  return (
    <DsModal
      open
      onClose={onClose}
      title="Lieferant einladen"
      description="Lieferanten suchen und mit Ansprechpartner der Ausschreibung zuordnen."
      footer={
        <>
          <DsButton onClick={onClose}>Fertig</DsButton>
          <DsButton
            variant="primary"
            disabled={!supplierId || addParticipants.isPending}
            onClick={handleAdd}
          >
            Hinzufügen
          </DsButton>
        </>
      }
    >
      <div className="sc-picker">
        <DsSearchInput
          value={q}
          onSearch={(v) => {
            setQ(v);
            setSupplierId(undefined);
            setContactId(undefined);
          }}
          debounce={250}
          placeholder="Lieferantenname oder -nr. (min. 2 Zeichen)…"
          autoFocus
        />
        <div className="sc-picker-list ds-scroll">
          {q.trim().length < 2 ? (
            <p className="sc-hint">Suchbegriff eingeben…</p>
          ) : isFetching ? (
            <p className="sc-hint">Sucht…</p>
          ) : (suppliers ?? []).length === 0 ? (
            <p className="sc-hint">Keine Lieferanten gefunden.</p>
          ) : (
            (suppliers ?? []).map((s) => {
              const added = existing.has(s.id);
              return (
                <label
                  key={s.id}
                  className={`sc-picker-row${added ? ' is-disabled' : ''}`}
                >
                  <input
                    type="radio"
                    name="sc-supplier"
                    disabled={added}
                    checked={supplierId === s.id}
                    onChange={() => {
                      setSupplierId(s.id);
                      setContactId(undefined);
                    }}
                  />
                  <span className="sc-picker-main">
                    <span className="sc-picker-title">{s.name}</span>
                    <span className="sc-picker-sub">Nr. {s.id}</span>
                  </span>
                  {added && (
                    <span className="ds-pill muted">bereits eingeladen</span>
                  )}
                </label>
              );
            })
          )}
        </div>
        {selectedSupplier && (
          <label className="sc-field">
            <span className="sc-field-label">
              Ansprechpartner für {selectedSupplier.name}
            </span>
            <select
              className="ds-input"
              value={contactId ?? ''}
              onChange={(e) =>
                setContactId(
                  e.target.value ? Number(e.target.value) : undefined,
                )
              }
            >
              <option value="">
                Erster Kontakt mit E-Mail (automatisch)
              </option>
              {(contacts ?? [])
                .filter((c) => c.email)
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} ({c.email})
                  </option>
                ))}
            </select>
          </label>
        )}
      </div>
    </DsModal>
  );
}
